import { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Label } from "../../../Components/ui";
import { notificationCenterService, type NotificationBenefitKind, type NotificationBenefitLink } from "../../../api/services/notification-center.service";
import { BENEFIT_LABELS, benefitVariableValues } from "./benefitTemplateBlueprints";
import { ListPagination } from "./NotificationListControls";

type Props = {
  kind: NotificationBenefitKind;
  variableKeys: string[];
  values: Record<string, string>;
  httpsKeys?: string[];
  onChange: (values: Record<string, string>) => void;
};

export default function NotificationBenefitSelector({ kind, variableKeys, values, onChange, httpsKeys = [] }: Props) {
  const label = BENEFIT_LABELS[kind];
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [rows, setRows] = useState<NotificationBenefitLink[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<NotificationBenefitLink | null>(null);
  const generation = useRef(0);
  const invalidate = () => { generation.current += 1; setRows([]); setLoading(true); };

  useEffect(() => {
    const version = ++generation.current;
    setLoading(true);
    setError("");
    setRows([]);
    const timer = setTimeout(() => {
      void notificationCenterService.getBenefitLinks({ kind, search, page, page_size: 20 })
        .then(({ data }) => {
          if (generation.current !== version) return;
          setRows(data.results); setCount(data.count);
        })
        .catch(() => {
          if (generation.current !== version) return;
          setCount(0); setError(`${label} 배포 링크를 불러오지 못했습니다.`);
        })
        .finally(() => { if (generation.current === version) setLoading(false); });
    }, 250);
    return () => { generation.current += 1; clearTimeout(timer); };
  }, [kind, label, search, page, refresh]);

  const matches = selected && Object.entries(benefitVariableValues(variableKeys, kind, selected, httpsKeys))
    .every(([key, value]) => values[key] === value);
  const activeSelection = matches ? selected : null;
  const options = activeSelection && !rows.some((row) => row.id === activeSelection.id)
    ? [activeSelection, ...rows] : rows;
  const select = (id: string) => {
    const next = options.find((row) => row.id === id);
    setSelected(next || null);
    onChange(benefitVariableValues(variableKeys, kind, next, httpsKeys));
  };

  return <fieldset className="border rounded p-3 mb-3">
    <legend className="fs-6 float-none w-auto px-1">기존 {label} 연결</legend>
    <div className="d-flex flex-wrap gap-2 mb-2">
      <Label htmlFor={`benefit-${kind}-search`} className="visually-hidden">{label} 검색</Label>
      <Input id={`benefit-${kind}-search`} value={search} maxLength={100} placeholder={`${label} 이름 검색`}
        onChange={(event) => { invalidate(); setPage(1); setSearch(event.target.value); }} />
      <Label htmlFor={`benefit-${kind}-select`} className="visually-hidden">기존 {label} 선택</Label>
      <Input id={`benefit-${kind}-select`} type="select" value={activeSelection?.id || ""} disabled={loading || Boolean(error)} onChange={(event) => select(event.target.value)}>
        <option value="">{loading ? "배포 링크 조회 중..." : `${label} 선택`}</option>
        {options.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.source === "event" ? "이벤트" : "공유"} · {row.end_date || "기한 없음"} · 배포 #{row.id.split(":").at(-1)}</option>)}
      </Input>
    </div>
    {error ? <Alert color="warning">{error} <Button size="sm" onClick={() => { invalidate(); setRefresh((value) => value + 1); }}>다시 조회</Button></Alert> : null}
    {!loading && !error && !rows.length ? <p className="small text-muted mb-0">선택할 배포 링크가 없습니다. 혜택 관리에서 배포 기간·공개 상태·잔여 수량을 확인해 주세요.</p> : null}
    {activeSelection ? <div className="small text-break mt-2" role="status">
      <strong>{activeSelection.name}</strong> · 1인 {activeSelection.per_person_limit}개
      {activeSelection.remaining_stock !== null ? ` · 잔여 ${activeSelection.remaining_stock}개` : ""}
      <div>{activeSelection.url}</div>
      <Button size="sm" color="light" className="mt-1" onClick={() => select("")}>{label} 연결 해제</Button>
    </div> : null}
    <ListPagination label={`${label} 배포 링크`} page={page} count={count} loading={loading} onChange={(next) => { invalidate(); setPage(next); }} />
  </fieldset>;
}
